// Imperative API for the global overlay primitives.
//
// Used by App.svelte (mounts <Overlays /> once) and by any page that
// wants to surface a success toast, error toast, stale-state overlay,
// or full-width error banner. We keep the API as a tiny pub-sub
// (subscribe + setter) instead of a Svelte store so consumers don't
// pull in `svelte/store` for one-off imperative calls — matches the
// design ADR-5 contract used by `lib/store.ts`.
//
// API:
//   - `subscribeOverlays(listener)`  — receives the current state on
//     registration + every change.
//   - `toastSuccess({ message })`   — returns id; auto-dismisses
//     after 4000ms.
//   - `toastError({ message })`     — returns id; manual dismiss only.
//   - `dismissToast(id)`            — removes the toast.
//   - `mountStaleOverlay()`         — show the persistent stale pill.
//   - `dismissStaleOverlay()`       — hide it.
//   - `mountErrorBanner({ message })`  — show the full-width banner.
//   - `dismissErrorBanner()`        — hide it.

export type ToastKind = "success" | "error";

export interface ToastState {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface OverlaysState {
  toasts: ToastState[];
  staleMounted: boolean;
  errorMessage: string | null;
}

const INITIAL_STATE: OverlaysState = {
  toasts: [],
  staleMounted: false,
  errorMessage: null,
};

let state: OverlaysState = { ...INITIAL_STATE };
const listeners: Set<(next: OverlaysState) => void> = new Set();
let toastSeq = 0;
// We hold the success-toast timers outside the serialisable state
// object so a plain object comparison via `set` still works.
const timers: Map<number, ReturnType<typeof setTimeout>> = new Map();

function emit(): void {
  for (const l of [...listeners]) l(state);
}

function set(next: OverlaysState): void {
  state = next;
  emit();
}

export function subscribeOverlays(
  listener: (next: OverlaysState) => void,
): () => void {
  listeners.add(listener);
  listener(state);
  return () => {
    listeners.delete(listener);
  };
}

export function toastSuccess(args: { message: string }): number {
  const id = ++toastSeq;
  const timer = setTimeout(() => dismissToast(id), 4000);
  timers.set(id, timer);
  set({ ...state, toasts: [...state.toasts, { id, kind: "success", message: args.message }] });
  return id;
}

export function toastError(args: { message: string }): number {
  const id = ++toastSeq;
  set({ ...state, toasts: [...state.toasts, { id, kind: "error", message: args.message }] });
  return id;
}

export function dismissToast(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
  set({ ...state, toasts: state.toasts.filter((t) => t.id !== id) });
}

export function mountStaleOverlay(): void {
  set({ ...state, staleMounted: true });
}

export function dismissStaleOverlay(): void {
  set({ ...state, staleMounted: false });
}

export function mountErrorBanner(args: { message: string }): void {
  set({ ...state, errorMessage: args.message });
}

export function dismissErrorBanner(): void {
  set({ ...state, errorMessage: null });
}

/** For test cleanup — clear all pending timers. Not exported via the
 *  public API surface; tests reach it via the vitest hoisting trick. */
export function _clearAllTimers(): void {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
}
